/* eslint-disable */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import MemberBadge from "../components/MemberBadge";

/**
 * Session 57 · Admin → Founding Members console.
 *
 * Three primary surfaces on one page:
 *   1. Cohort snapshot (seats used, activation rate, funnel)
 *   2. Actions row: single invite, CSV bulk, send test email
 *   3. Members table with per-row resend + timeline drilldown
 */
export default function AdminFoundingMembers() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // single invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteCity, setInviteCity] = useState("");
  const [sendNow, setSendNow] = useState(true);
  const [lastInvite, setLastInvite] = useState(null);

  // csv
  const [csv, setCsv] = useState("");
  const [csvResult, setCsvResult] = useState(null);

  // test email
  const [testTo, setTestTo] = useState("");

  // Session 58 · delivery recovery — list of founding members whose invite
  // email never landed, and the result of the one-click bulk-retry.
  const [undelivered, setUndelivered] = useState(null);
  const [recoveryResult, setRecoveryResult] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, r, u] = await Promise.all([
        api.get("/admin/founding-members/stats"),
        api.get("/admin/founding-members"),
        api.get("/admin/founding-members/undelivered"),
      ]);
      setStats(s.data);
      setRows(r.data || []);
      setUndelivered(u.data);
    } catch (e) { /* handled by toast */ }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 4000);
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/founding-members/invite", {
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined,
        city: inviteCity.trim() || undefined,
        send_email: sendNow,
      });
      setLastInvite(r.data);
      if (r.data?.error) {
        showToast("error", r.data.error);
      } else {
        showToast("ok", sendNow
          ? `Invitation emailed to ${r.data.email}.`
          : `Invitation created for ${r.data.email} (email not sent).`);
      }
      setInviteEmail(""); setInviteName(""); setInviteCity("");
      reload();
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Invite failed.");
    } finally { setBusy(false); }
  };

  const importCsv = async () => {
    if (!csv.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/founding-members/bulk-csv", {
        csv_text: csv, send_email: sendNow,
      });
      setCsvResult(r.data);
      showToast("ok", `Processed ${r.data.count} rows.`);
      setCsv("");
      reload();
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "CSV import failed.");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/founding-members/test-email", { to: testTo.trim() });
      if (r.data?.ok) {
        showToast("ok", `Preview sent to ${testTo}. Temp password shown: ${r.data.temp_password_previewed}`);
      } else {
        // Session 58 · surface the exact Resend rejection reason (returned by
        // the backend as `error`) so the admin doesn't have to open Resend logs.
        const reason = r.data?.error ? ` — ${r.data.error}` : " — check Resend logs.";
        showToast("error", `Preview send failed${reason}`);
      }
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Send failed.");
    } finally { setBusy(false); }
  };

  const resend = async (row) => {
    if (!window.confirm(`Regenerate temp password + resend invitation to ${row.email}?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/founding-members/${row.user_id}/resend`);
      if (r.data?.ok) {
        showToast("ok", `Resent to ${row.email}. New temp password: ${r.data.temp_password}`);
      } else {
        // Session 58 · endpoint returns ok:false when Resend rejected the send.
        // Surface the real reason instead of the misleading "Resent." toast.
        const reason = r.data?.error ? ` — ${r.data.error}` : "";
        showToast("error", `Resend failed for ${row.email}${reason}`);
      }
      reload();
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Resend failed.");
    } finally { setBusy(false); }
  };

  // Session 58 · one-click bulk resend for every founding member whose
  // invitation email has NOT been delivered yet.
  const resendAllUndelivered = async () => {
    const n = undelivered?.count || 0;
    if (!n) return;
    if (!window.confirm(`Regenerate temp passwords and re-send the invitation email to all ${n} undelivered founding member${n === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/founding-members/resend-all-undelivered");
      setRecoveryResult(r.data);
      showToast(r.data.sent > 0 ? "ok" : "error",
        `${r.data.sent} sent · ${r.data.failed} failed.`);
      reload();
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Bulk resend failed.");
    } finally { setBusy(false); }
  };

  if (!user || user.role !== "admin") {
    return <div style={{ padding: 40 }}>Admin access required.</div>;
  }

  return (
    <div data-testid="admin-founding-members-page" style={{ minHeight: "100vh", background: "#FAF8F3", padding: "40px 24px 80px", fontFamily: "Helvetica,Arial,sans-serif", color: "#1B2340" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
          <div>
            <div style={{ letterSpacing: "0.22em", color: "#B8913A", fontSize: 11, fontWeight: 700 }}>MÉTIER · ADMIN</div>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 34, margin: "6px 0 0" }}>Founding Members</h1>
            <p style={{ fontSize: 14, color: "rgba(27,35,64,0.6)", margin: "6px 0 0" }}>
              Invitation-only. Permanent. Capped at {stats?.cohort_cap || 100} seats.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Link to="/admin/alpha" style={ghostLink} data-testid="link-alpha-dashboard">Alpha Dashboard →</Link>
            <Link to="/admin/members" style={ghostLink} data-testid="link-all-members">All Members →</Link>
          </div>
        </div>

        {/* Snapshot cards */}
        <div style={grid4}>
          <StatCard label="Seats used" value={`${stats?.founding_members ?? "—"} / ${stats?.cohort_cap ?? 100}`} sub={`${stats?.seats_remaining ?? "—"} remaining`} />
          <StatCard label="Activated" value={stats?.activated ?? "—"} sub={`${stats?.activation_rate_pct ?? 0}% activation rate`} />
          <StatCard label="Invitations sent" value={stats?.invitations_sent ?? "—"} sub={`${stats?.acceptance_rate_pct ?? 0}% acceptance`} />
          <StatCard label="Profiles complete" value={stats?.profiles_complete ?? "—"} sub={`${stats?.profile_completion_pct ?? 0}%`} />
        </div>

        {/* Action panels */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 22 }}>
          <Panel title="Send single invite">
            <label style={inputLabel}>Email *</label>
            <input data-testid="invite-email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="jane@example.com" style={inputSm} />
            <label style={inputLabel}>Name</label>
            <input data-testid="invite-name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Jane Doe" style={inputSm} />
            <label style={inputLabel}>City</label>
            <input data-testid="invite-city" value={inviteCity} onChange={(e) => setInviteCity(e.target.value)} placeholder="Portsmouth, NH" style={inputSm} />
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginTop: 10 }}>
              <input type="checkbox" checked={sendNow} onChange={(e) => setSendNow(e.target.checked)} data-testid="invite-send-now" />
              Send invitation email immediately
            </label>
            <button data-testid="invite-submit" onClick={invite} disabled={busy || !inviteEmail} style={{ ...btnPrimary, opacity: busy || !inviteEmail ? 0.5 : 1, marginTop: 14 }}>
              Create invitation
            </button>
            {lastInvite && !lastInvite.error && (
              <div style={{
                marginTop: 12, padding: 10, borderRadius: 8,
                background: "#FAF8F3", border: "1px solid rgba(184,145,58,0.4)", fontSize: 12,
              }}>
                <div style={{ color: "#B8913A", fontWeight: 700, marginBottom: 4 }}>TEMP PASSWORD (copy now — not shown again)</div>
                <code data-testid="last-invite-temp-password" style={{ fontSize: 14, fontWeight: 600 }}>{lastInvite.temp_password}</code>
              </div>
            )}
          </Panel>

          <Panel title="Bulk CSV import">
            <p style={sub}>Columns: <code>email,name,city</code>. Header row required.</p>
            <textarea
              data-testid="csv-textarea"
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={6}
              placeholder={"email,name,city\njane@example.com,Jane Doe,Portsmouth NH\njohn@example.com,John Doe,Concord NH"}
              style={{ ...inputSm, minHeight: 130, fontFamily: "monospace", fontSize: 12 }}
            />
            <button data-testid="csv-submit" onClick={importCsv} disabled={busy || !csv.trim()} style={{ ...btnPrimary, opacity: busy || !csv.trim() ? 0.5 : 1, marginTop: 10 }}>
              Import
            </button>
            {csvResult && <CsvResultPanel result={csvResult} />}
          </Panel>

          <Panel title="Send test email">
            <p style={sub}>Preview the branded invitation template. No account is created.</p>
            <label style={inputLabel}>To</label>
            <input data-testid="test-email-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" style={inputSm} />
            <button data-testid="test-email-submit" onClick={sendTest} disabled={busy || !testTo} style={{ ...btnPrimary, opacity: busy || !testTo ? 0.5 : 1, marginTop: 12 }}>
              Send test
            </button>
          </Panel>
        </div>

        {/* Session 58 · Delivery recovery panel — surfaces the count of
            founding members who never received their invite and offers a
            one-click bulk retry once the Resend config is repaired. */}
        {undelivered && undelivered.count > 0 && (
          <div data-testid="undelivered-panel" style={{
            marginTop: 24, background: "#FDF1EC", border: "1px solid #E5A0A0",
            borderRadius: 12, padding: "20px 24px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B84A3A", fontWeight: 700 }}>Delivery recovery</div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 22, margin: "6px 0 0", fontWeight: 500 }}>
                  {undelivered.count} founding member{undelivered.count === 1 ? "" : "s"} never received their invitation
                </h3>
                <p style={{ fontSize: 13, color: "rgba(27,35,64,0.75)", margin: "6px 0 0", maxWidth: 620 }}>
                  These accounts exist in the platform but the welcome email failed (Resend rejection, unverified domain, etc.). Once you've fixed the underlying config, click below to regenerate temp passwords AND retry the email for every one of them in a single batch.
                </p>
              </div>
              <button type="button" onClick={resendAllUndelivered} disabled={busy}
                data-testid="resend-all-undelivered"
                style={{
                  padding: "10px 22px", background: busy ? "rgba(184,145,58,0.5)" : "#B8913A",
                  color: "#FAF8F3", border: "none", borderRadius: 999,
                  cursor: busy ? "not-allowed" : "pointer",
                  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                }}>
                {busy ? "Working…" : `Resend all ${undelivered.count}`}
              </button>
            </div>

            {recoveryResult && <CsvResultPanel result={{
              count: recoveryResult.count,
              results: recoveryResult.results,
            }} />}
          </div>
        )}

        {/* Members table */}
        <div style={{ marginTop: 26, background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(27,35,64,0.06)", display: "flex", justifyContent: "space-between" }}>
            <strong>Roster</strong>
            <span style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{rows.length} member{rows.length === 1 ? "" : "s"}</span>
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>No Founding Members yet. Send the first invitation above.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAF8F3", textAlign: "left", color: "rgba(27,35,64,0.7)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <th style={th}>Name</th>
                  <th style={th}>Status</th>
                  <th style={th}>Delivery</th>
                  <th style={th}>Invited</th>
                  <th style={th}>First login</th>
                  <th style={th}>Bookings</th>
                  <th style={th}>Commons</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} data-testid={`founding-row-${r.user_id}`} style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name || "—"}</div>
                      <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{r.email}</div>
                    </td>
                    <td style={td}>
                      {r.must_change_password
                        ? <span style={pillPending}>Pending</span>
                        : <span style={pillActive}>Activated</span>}
                    </td>
                    <td style={td}>
                      {/* Session 58 · colored delivery pill:
                          🟢 activated  🟢 delivered  🔴 failed */}
                      <DeliveryPill status={r.delivery_status} />
                      {r.delivery_status === "delivered" && r.invitation_email_id && (
                        <div style={{ fontSize: 10, color: "rgba(27,35,64,0.5)", fontFamily: "ui-monospace, monospace", marginTop: 3 }}>
                          {r.invitation_email_id.slice(0, 8)}…
                        </div>
                      )}
                    </td>
                    <td style={td}>{fmt(r.invitation_sent_at)}</td>
                    <td style={td}>{fmt(r.first_login_at)}</td>
                    <td style={td}>{r.bookings ?? 0}</td>
                    <td style={td}>{r.commons_posts ?? 0}</td>
                    <td style={td}>
                      <Link to={`/admin/members/${r.user_id}`} style={ghostLink}>Timeline</Link>
                      <button onClick={() => resend(r)} style={{ ...btnSecondary, marginLeft: 8 }} data-testid={`resend-${r.user_id}`}>Resend</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, padding: "12px 18px",
          background: toast.kind === "ok" ? "#1B4634" : "#7A1B22",
          color: "#fff", borderRadius: 12, fontSize: 13, maxWidth: 460,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)", zIndex: 99,
        }}>{toast.text}</div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: "#fff", padding: "18px 20px", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 28, fontWeight: 500, marginTop: 6, color: "#1B2340" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "rgba(27,35,64,0.6)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", padding: "18px 20px" }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B8913A", fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return "—"; }
}

// ---- styles ----
const grid4 = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 };
const inputSm = { width: "100%", padding: "9px 12px", border: "1px solid rgba(27,35,64,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", boxSizing: "border-box", marginTop: 4 };
const inputLabel = { display: "block", fontSize: 11, letterSpacing: "0.08em", color: "rgba(27,35,64,0.65)", textTransform: "uppercase", fontWeight: 600, marginTop: 8 };
const btnPrimary = { padding: "9px 18px", background: "#1B2340", color: "#fff", border: "none", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", letterSpacing: "0.04em" };
const btnSecondary = { padding: "6px 12px", background: "#fff", color: "#1B2340", border: "1px solid rgba(27,35,64,0.2)", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const ghostLink = { fontSize: 13, color: "#B8913A", textDecoration: "none", fontWeight: 600 };
const th = { padding: "10px 16px" };
const td = { padding: "12px 16px", verticalAlign: "top" };
const sub = { fontSize: 12, color: "rgba(27,35,64,0.65)", margin: "0 0 8px" };
const pillPending = { display: "inline-block", padding: "2px 8px", background: "#FFF3D6", color: "#7C5B10", fontSize: 11, borderRadius: 999, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" };
const pillActive = { display: "inline-block", padding: "2px 8px", background: "#DFEEE1", color: "#1B4634", fontSize: 11, borderRadius: 999, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" };


// Session 58 · Colored delivery-status pill for the founding roster.
// Traffic-light semantics per user request:
//   🟢 activated  — member logged in at least once
//   🟢 delivered  — Resend accepted the send (has invitation_email_id)
//   🔴 failed     — no email_id, no first login = never received it
function DeliveryPill({ status }) {
  const map = {
    activated: { bg: "#DFEEE1", fg: "#1B4634", label: "🟢 Activated" },
    delivered: { bg: "#E4F1E8", fg: "#1F5C3F", label: "🟢 Delivered" },
    queued:    { bg: "#FFF3D6", fg: "#7C5B10", label: "🟡 Queued" },
    failed:    { bg: "#FBE4E0", fg: "#7A1B22", label: "🔴 Not delivered" },
  };
  const s = map[status] || map.failed;
  return (
    <span data-testid={`delivery-pill-${status}`} style={{
      display: "inline-block", padding: "2px 10px",
      background: s.bg, color: s.fg,
      fontSize: 11, borderRadius: 999, fontWeight: 700,
      letterSpacing: "0.04em",
    }}>{s.label}</span>
  );
}


// Session 58 · Full result surface for the CSV bulk import.
// Shows delivery breakdown, per-row status + Resend error, and a CSV export
// so the admin can rescue temp passwords for any failed sends.
function CsvResultPanel({ result }) {
  const [showAll, setShowAll] = useState(false);
  const rows = result.results || [];
  const total = result.count || rows.length;
  const sent = rows.filter((r) => r.invitation_sent).length;
  const failed = rows.filter((r) => r.error || (!r.invitation_sent && !r.already_existed)).length;
  const existed = rows.filter((r) => r.already_existed).length;

  const downloadCsv = () => {
    const header = "email,name,temp_password,status,delivery,error\n";
    const body = rows.map((r) => {
      const status = r.already_existed ? "existing" : "created";
      const delivery = r.invitation_sent ? "sent" : (r.error ? "failed" : "not_sent");
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      return [r.email, r.name || "", r.temp_password, status, delivery, r.error || ""]
        .map(escape).join(",");
    }).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `founding-invites-${new Date().toISOString().slice(0,19).replace(/[:T]/g,"-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const visible = showAll ? rows : rows.filter((r) => r.error || !r.invitation_sent);

  return (
    <div data-testid="csv-result-panel" style={{ marginTop: 12 }}>
      <div style={{
        background: failed > 0 ? "#FDF1EC" : "#EFF6EB",
        border: `1px solid ${failed > 0 ? "#E5A0A0" : "#C7E0B5"}`,
        borderRadius: 10, padding: "12px 14px", fontSize: 12,
      }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 18, marginBottom: 4 }}>
          Processed <b>{total}</b> row{total === 1 ? "" : "s"}
        </div>
        <div style={{ color: "rgba(27,35,64,0.75)", lineHeight: 1.7 }}>
          <b style={{ color: "#1B4634" }}>{sent} sent</b>
          {failed > 0 && <> · <b style={{ color: "#B84A3A" }}>{failed} failed</b></>}
          {existed > 0 && <> · <b>{existed} already existed</b></>}
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button type="button" onClick={downloadCsv}
            data-testid="csv-download-result"
            style={{ padding: "6px 14px", background: "#1B2340", color: "#fff",
              border: "none", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Download all temp passwords (CSV)
          </button>
          {rows.some((r) => r.error) && (
            <button type="button" onClick={() => setShowAll((v) => !v)}
              style={{ padding: "6px 14px", background: "#fff", color: "#1B2340",
                border: "1px solid rgba(27,35,64,0.25)", borderRadius: 999,
                fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {showAll ? "Failed only" : `Show all ${total}`}
            </button>
          )}
        </div>
      </div>

      {visible.length > 0 && (
        <div style={{ marginTop: 10, maxHeight: 320, overflow: "auto",
          border: "1px solid rgba(27,35,64,0.1)", borderRadius: 10, background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "rgba(184,145,58,0.08)", textAlign: "left" }}>
                <th style={{ padding: "8px 10px" }}>Email</th>
                <th style={{ padding: "8px 10px" }}>Temp password</th>
                <th style={{ padding: "8px 10px" }}>Delivery</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={i} data-testid={`csv-result-row-${i}`}
                  style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }}>
                  <td style={{ padding: "7px 10px", fontFamily: "ui-monospace, monospace" }}>{r.email}</td>
                  <td style={{ padding: "7px 10px", fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>
                    {r.temp_password || "—"}
                  </td>
                  <td style={{ padding: "7px 10px", color: r.error ? "#B84A3A"
                    : r.invitation_sent ? "#1B4634" : "rgba(27,35,64,0.7)" }}>
                    {r.invitation_sent ? "sent" : r.error ? "failed" : "not sent"}
                    {r.error && <div style={{ fontSize: 10, color: "#B84A3A", marginTop: 2 }}>{r.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
